import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { 
  HelpCircle, 
  Clock, 
  Plus, 
  Trash2, 
  Save, 
  ChevronDown, 
  ChevronUp 
} from 'lucide-react';
import './FormsUnified.css';

const DEFAULT_FORM_DATA = {
  kicker: 'FAQ',
  title: 'Quick answers about school life',
  description: 'Get fast answers about curriculum, uniforms, school hours, and more.',
  items: [
    {
      question: 'About Curriculum',
      answer: 'We blend national standards with project-based learning and character education.'
    },
    {
      question: 'Uniforms',
      answer: 'Uniform guidelines are shared during enrollment and updated every academic year.'
    },
    {
      question: 'School Hours',
      answer: 'Regular classes run Monday to Friday with half-day sessions on Saturday.'
    },
    {
      question: 'Admissions Timeline',
      answer: 'Admissions open year-round with priority windows for new academic years.'
    }
  ]
};

export default function ContactFaqForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false,
    items: false
  });
  const [pendingItems, setPendingItems] = useState({});
  const [pendingDeletes, setPendingDeletes] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [expandedIndex, setExpandedIndex] = useState(0);

  const getAuthHeaders = useCallback(() => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }, []);

  const loadSection = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=contact-us`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'contact-faq' || s.section_type === 'faq');
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=contact-us`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => s.section_type === 'contact-faq' || s.section_type === 'faq');
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);
        const rawContent = foundSection.content_published || foundSection.content;
        let parsed = rawContent;
        if (typeof rawContent === 'string') {
          try {
            parsed = JSON.parse(rawContent);
          } catch (e) {
            console.error('Failed to parse contact-faq content', e);
          }
        }

        const dataEn = parsed?.en || parsed || {};
        const newFormData = {
          kicker: dataEn.kicker || 'FAQ',
          title: dataEn.title || 'Quick answers about school life',
          description: dataEn.description || 'Get fast answers about curriculum, uniforms, school hours, and more.',
          items: Array.isArray(dataEn.items) && dataEn.items.length > 0 ? dataEn.items : (
            Array.isArray(dataEn.faqs) && dataEn.faqs.length > 0 ? dataEn.faqs : DEFAULT_FORM_DATA.items
          )
        };

        setFormData(newFormData);
        setOriginalData(newFormData);

        // Fetch pending changes
        try {
          const pendRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendRes.ok) {
            const pendJson = await pendRes.json();
            if (pendJson.success && Array.isArray(pendJson.data?.pending_changes)) {
              const pendMap = { kicker: false, title: false, description: false, items: false };
              const itemPendMap = {};
              const deletePendMap = {};

              pendJson.data.pending_changes.forEach(change => {
                if (change.status === 'pending') {
                  if (pendMap[change.field_id] !== undefined) {
                    pendMap[change.field_id] = true;
                  }
                  if (change.field_id === 'items' || change.field_id === 'faqs') {
                    pendMap.items = true;
                  }
                  if (change.field_id?.includes('items.')) {
                    const m = change.field_id.match(/items\.(\d+)\.(add|update|delete)/);
                    if (m) {
                      const idx = m[1];
                      const op = m[2];
                      if (op === 'delete') {
                        deletePendMap[`item_${idx}`] = true;
                      } else {
                        itemPendMap[`item_${idx}`] = true;
                      }
                    } else {
                      const mOld = change.field_id.match(/items\.(\d+)/);
                      if (mOld) itemPendMap[`item_${mOld[1]}`] = true;
                    }
                  }
                }
              });
              setPendingFields(pendMap);
              setPendingItems(itemPendMap);
              setPendingDeletes(deletePendMap);
            }
          }
        } catch (err) {
          console.warn('Could not load pending changes for contact-faq', err);
        }
      }
    } catch (err) {
      console.error('Error loading contact-faq section:', err);
      toast.error('Failed to load contact FAQ data');
    } finally {
      setLoading(false);
    }
  }, [apiBase, getAuthHeaders]);

  useEffect(() => {
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFaqItemChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.items];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return { ...prev, items: updated };
    });
  };

  const handleAddFaqItem = () => {
    const newItem = {
      question: 'Pertanyaan Baru',
      answer: 'Tulis penjelasan jawaban lengkap di sini...',
      isNew: true
    };
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    setExpandedIndex(formData.items.length);
    toast.info('Item FAQ baru ditambahkan ke draf. Isi data dan klik "Save Butir Ini".');
  };

  const handleRemoveFaqItem = async (index) => {
    const itemToRemove = formData.items[index];

    // If item is new/draft not saved on server yet, remove locally
    if (itemToRemove.isNew && (!originalData.items || !originalData.items[index])) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, idx) => idx !== index)
      }));
      toast.info('Item FAQ baru dibatalkan');
      return;
    }

    if (formData.items.length <= 1) {
      toast.warning('Minimal harus ada 1 pertanyaan FAQ');
      return;
    }

    if (!window.confirm(`Ajukan penghapusan FAQ "${itemToRemove.question || 'Untitled'}" ke Kepala Sekolah?`)) {
      return;
    }

    if (!sectionId) {
      toast.error('Section ID tidak ditemukan di database');
      return;
    }

    const key = `del_${index}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${index}.delete`,
          field_name: `Hapus FAQ: ${itemToRemove.question}`,
          old_value: originalData.items?.[index] || itemToRemove,
          new_value: { action: 'delete', question: itemToRemove.question, answer: itemToRemove.answer },
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Pengajuan hapus FAQ "${itemToRemove.question}" berhasil dikirim untuk approval`);
        setPendingDeletes(prev => ({ ...prev, [`item_${index}`]: true }));
        try {
          sessionStorage.removeItem('cms_page_contact_us');
          sessionStorage.removeItem('cms_sections_contact_us');
          window.dispatchEvent(new Event('cms-contact-us-updated'));
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[ContactFaqForm] Submit delete error:', err);
      toast.error('Gagal mengajukan hapus FAQ: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSubmitField = async (fieldId, proposedValue, currentVal, fieldDisplayName) => {
    if (!sectionId) {
      toast.error('Section not found in database. Please run migrations first.');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldId]: true }));
    try {
      const payload = {
        section_id: sectionId,
        field_id: fieldId,
        field_name: fieldDisplayName || (fieldId.charAt(0).toUpperCase() + fieldId.slice(1)),
        current_value: typeof currentVal === 'object' ? JSON.stringify(currentVal) : String(currentVal || ''),
        proposed_value: typeof proposedValue === 'object' ? JSON.stringify(proposedValue) : String(proposedValue || ''),
        old_value: currentVal !== undefined ? currentVal : originalData[fieldId],
        new_value: proposedValue,
        locale: 'en'
      };

      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const json = await res.json();
      if (res.ok && (json.success || json.ok)) {
        toast.success(`Perubahan "${fieldDisplayName || fieldId}" berhasil diajukan untuk approval`);
        setPendingFields(prev => ({ ...prev, [fieldId]: true }));
        setOriginalData(prev => ({ ...prev, [fieldId]: proposedValue }));
        try {
          sessionStorage.removeItem('cms_page_contact_us');
          sessionStorage.removeItem('cms_sections_contact_us');
          window.dispatchEvent(new Event('cms-contact-us-updated'));
        } catch (e) {}
      } else {
        throw new Error(json.message || 'Submission failed');
      }
    } catch (err) {
      console.error('Submit field error:', err);
      toast.error(err.message || 'Failed to submit changes');
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  const handleSubmitItem = async (index) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan di database');
      return;
    }

    const item = formData.items[index];
    const key = `item_${index}`;
    const isNew = item.isNew || !originalData.items || !originalData.items[index];
    const fieldId = isNew ? `items.${index}.add` : `items.${index}.update`;

    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      const cleanItem = { ...item };
      delete cleanItem.isNew;

      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldId,
          field_name: `FAQ Item: ${item.question || `Item ${index + 1}`}`,
          old_value: isNew ? null : (originalData.items?.[index] || {}),
          new_value: cleanItem,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`FAQ "${item.question}" berhasil diajukan untuk approval`);
        setPendingItems(prev => ({ ...prev, [key]: true }));
        setOriginalData(prev => {
          const next = [...(prev.items || [])];
          next[index] = cleanItem;
          return { ...prev, items: next };
        });
        setFormData(prev => {
          const next = [...prev.items];
          next[index] = cleanItem;
          return { ...prev, items: next };
        });
        try {
          sessionStorage.removeItem('cms_page_contact_us');
          sessionStorage.removeItem('cms_sections_contact_us');
          window.dispatchEvent(new Event('cms-contact-us-updated'));
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[ContactFaqForm] Submit item error:', err);
      toast.error('Gagal mengajukan butir FAQ: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Frequently Asked Questions...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />
      
      {/* Standard Unified Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <HelpCircle size={22} />
        </div>
        <div>
          <h2 className="unified-title">Frequently Asked Questions (FAQ)</h2>
          <p className="unified-subtitle">
            Kelola daftar pertanyaan umum mengenai kurikulum, seragam, jam sekolah, pendaftaran, dan kebijakan sekolah.
          </p>
        </div>
        {sectionId && (
          <span className="unified-section-badge">ID: {sectionId}</span>
        )}
      </div>

      <div className="unified-form-body">
        {/* Kicker Field */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Section Kicker</label>
              <p className="unified-hint">Badge tag kecil di atas judul utama FAQ.</p>
            </div>
            {pendingFields.kicker && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              className="unified-input"
              value={formData.kicker}
              onChange={(e) => handleInputChange('kicker', e.target.value)}
              disabled={pendingFields.kicker}
              placeholder="e.g. FAQ"
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('kicker', formData.kicker, originalData.kicker, 'Section Kicker')}
              disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
            >
              <Save size={14} /> {submitting.kicker ? 'Saving...' : (pendingFields.kicker ? 'Pending' : 'Save')}
            </button>
          </div>
        </div>

        {/* Title Field */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Section Title</label>
              <p className="unified-hint">Judul besar bagian FAQ.</p>
            </div>
            {pendingFields.title && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              className="unified-input"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              disabled={pendingFields.title}
              placeholder="e.g. Quick answers about school life"
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('title', formData.title, originalData.title, 'Section Title')}
              disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
            >
              <Save size={14} /> {submitting.title ? 'Saving...' : (pendingFields.title ? 'Pending' : 'Save')}
            </button>
          </div>
        </div>

        {/* Description Field */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Section Description</label>
              <p className="unified-hint">Paragraf pengantar di bawah judul FAQ.</p>
            </div>
            {pendingFields.description && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>
          <div className="unified-input-action-column">
            <textarea
              className="unified-textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              disabled={pendingFields.description}
              placeholder="Get fast answers about curriculum, uniforms, school hours, and more."
            />
            <div className="unified-align-right">
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('description', formData.description, originalData.description, 'Section Description')}
                disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
              >
                <Save size={14} /> {submitting.description ? 'Saving...' : (pendingFields.description ? 'Pending' : 'Save Description')}
              </button>
            </div>
          </div>
        </div>

        {/* FAQ Items Accordion Manager */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Daftar Pertanyaan FAQ ({formData.items.length} Butir)</label>
              <p className="unified-hint">Kelola butir pertanyaan dan jawaban yang dapat diperluas oleh pengunjung situs.</p>
            </div>
            <button
              type="button"
              className="unified-secondary-btn"
              onClick={handleAddFaqItem}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Plus size={16} /> Tambah Pertanyaan
            </button>
          </div>

          <div className="unified-items-accordion-list">
            {formData.items.map((item, idx) => {
              const isExpanded = expandedIndex === idx;
              const isPending = pendingItems[`item_${idx}`];
              const isDeletePending = pendingDeletes[`item_${idx}`];
              const isSubmitting = submitting[`item_${idx}`] || submitting[`del_${idx}`];

              return (
                <div 
                  key={idx} 
                  className="unified-accordion-item-card"
                  style={{
                    border: isDeletePending 
                      ? '1.5px dashed #ef4444' 
                      : (isPending ? '1.5px dashed #f59e0b' : '1px solid #e2e8f0'),
                    background: isDeletePending ? '#fef2f2' : (isPending ? '#fffdf7' : '#ffffff')
                  }}
                >
                  <div 
                    className="unified-accordion-header"
                    onClick={() => setExpandedIndex(isExpanded ? -1 : idx)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="unified-accordion-title-wrap">
                      <span className="unified-accordion-num">#{idx + 1}</span>
                      <strong>{item.question || 'Pertanyaan Baru'}</strong>
                    </div>

                    <div className="unified-accordion-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {isDeletePending && (
                        <span className="unified-pending-pill" style={{ background: '#fee2e2', color: '#b91c1c' }}>
                          <Clock size={11} /> Pending Delete
                        </span>
                      )}
                      {!isDeletePending && isPending && (
                        <span className="unified-pending-pill">
                          <Clock size={11} /> Pending Approval
                        </span>
                      )}

                      <button
                        type="button"
                        className="unified-danger-icon-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFaqItem(idx);
                        }}
                        disabled={isDeletePending || isSubmitting}
                        style={{
                          background: isDeletePending ? '#fee2e2' : 'none',
                          border: 'none',
                          color: '#ef4444',
                          cursor: isDeletePending ? 'not-allowed' : 'pointer',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '11px'
                        }}
                        title="Hapus Pertanyaan"
                      >
                        <Trash2 size={15} />
                        {isDeletePending && <span>Pending Delete</span>}
                      </button>

                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="unified-accordion-content" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div>
                        <label className="unified-label" style={{ fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                          Pertanyaan (Question) *
                        </label>
                        <input
                          type="text"
                          className="unified-input"
                          value={item.question || ''}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => handleFaqItemChange(idx, 'question', e.target.value)}
                          placeholder="e.g. Bagaimana ketentuan seragam sekolah?"
                        />
                      </div>

                      <div>
                        <label className="unified-label" style={{ fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                          Jawaban (Answer) *
                        </label>
                        <textarea
                          className="unified-textarea"
                          rows={3}
                          value={item.answer || ''}
                          disabled={isDeletePending || isPending}
                          onChange={(e) => handleFaqItemChange(idx, 'answer', e.target.value)}
                          placeholder="Tuliskan jawaban yang jelas dan informatif untuk orang tua/siswa..."
                        />
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
                        <button
                          type="button"
                          className="unified-submit-button"
                          onClick={() => handleSubmitItem(idx)}
                          disabled={isDeletePending || isPending || isSubmitting}
                          style={{ padding: '6px 14px', fontSize: '12px' }}
                        >
                          <Save size={13} />
                          {isSubmitting ? 'Saving...' : (isDeletePending ? 'Pending Delete' : (isPending ? 'Pending' : 'Save Butir FAQ'))}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
